<template>
  <Head title="System Roles" />

  <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div class="flex items-center gap-3">
            <Link :href="route('team.index')">
              <Button variant="ghost" size="sm">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Button>
            </Link>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">System Roles</h1>
          </div>
          <p class="mt-1 text-sm text-gray-600 dark:text-gray-400 ml-14">
            Overview of all system roles, their permissions, and user assignments
          </p>
        </div>
      </div>

      <!-- Roles Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card v-for="role in roles" :key="role.id" class="hover:shadow-lg transition-shadow">
          <div class="space-y-4">
            <!-- Role Header -->
            <div class="flex items-start justify-between">
              <div class="flex items-center gap-3">
                <div :class="[
                  'p-3 rounded-lg',
                  getRoleIconBgColor(role.slug)
                ]">
                  <svg class="w-6 h-6" :class="getRoleIconColor(role.slug)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ role.name }}</h3>
                  <Badge :variant="getRoleBadgeVariant(role.slug)" size="sm" class="mt-1">
                    {{ role.user_count }} {{ role.user_count === 1 ? 'user' : 'users' }}
                  </Badge>
                </div>
              </div>
            </div>

            <!-- Role Description -->
            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div v-if="role.description" class="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <div v-html="formatDescription(role.description)"></div>
              </div>
              <div v-else class="text-sm text-gray-500 dark:text-gray-500 italic">
                No description available for this role.
              </div>
            </div>

            <!-- Key Capabilities -->
            <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Key Capabilities:</h4>
              <div class="space-y-2">
                <div v-for="capability in getRoleCapabilities(role.slug)" :key="capability" class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-green-500 dark:text-green-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span class="text-xs text-gray-600 dark:text-gray-400">{{ capability }}</span>
                </div>
              </div>
            </div>

          </div>
        </Card>
      </div>

      <!-- Empty State -->
      <Card v-if="!roles || roles.length === 0">
        <div class="text-center py-12">
          <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No Roles Found</h3>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">There are no system roles configured.</p>
        </div>
      </Card>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Badge from '@/Components/Common/Badge.vue';

const props = defineProps({
  roles: {
    type: Array,
    default: () => [],
  },
});

/**
 * Get role icon background color
 */
const getRoleIconBgColor = (slug) => {
  const colors = {
    'super_admin': 'bg-red-100 dark:bg-red-900/30',
    'company_admin': 'bg-blue-100 dark:bg-blue-900/30',
    'company_subscriber': 'bg-green-100 dark:bg-green-900/30',
    'company_user': 'bg-gray-100 dark:bg-gray-700',
    'support_agent': 'bg-purple-100 dark:bg-purple-900/30',
    'department_head': 'bg-yellow-100 dark:bg-yellow-900/30',
  };
  return colors[slug] || 'bg-gray-100 dark:bg-gray-700';
};

/**
 * Get role icon color
 */
const getRoleIconColor = (slug) => {
  const colors = {
    'super_admin': 'text-red-600 dark:text-red-400',
    'company_admin': 'text-blue-600 dark:text-blue-400',
    'company_subscriber': 'text-green-600 dark:text-green-400',
    'company_user': 'text-gray-600 dark:text-gray-400',
    'support_agent': 'text-purple-600 dark:text-purple-400',
    'department_head': 'text-yellow-600 dark:text-yellow-400',
  };
  return colors[slug] || 'text-gray-600 dark:text-gray-400';
};

/**
 * Get role badge variant
 */
const getRoleBadgeVariant = (slug) => {
  const variants = {
    'super_admin': 'danger',
    'company_admin': 'primary',
    'company_subscriber': 'success',
    'company_user': 'secondary',
    'support_agent': 'info',
    'department_head': 'warning',
  };
  return variants[slug] || 'secondary';
};

/**
 * Format description with line breaks and lists
 */
const formatDescription = (description) => {
  if (!description) return '';

  // Convert newlines to <br> tags
  let formatted = description
    .replace(/\n/g, '<br>')
    .replace(/\*\s+/g, '<br>• '); // Convert asterisk lists to bullet points

  return formatted;
};

/**
 * Get role capabilities based on actual database permissions
 */
const getRoleCapabilities = (slug) => {
  const capabilities = {
    'company_subscriber': [
      'Full system access and control',
      'Manage billing and subscriptions',
      'Manage all users, roles, and permissions',
      'Access all modules (IT, Maintenance, Incidents)',
      'Cannot be edited by other admins',
    ],
    'system_admin': [
      'Full system access and control',
      'Manage all users and permissions',
      'Configure all system settings',
      'Access all modules and features',
      'View and export all reports',
    ],
    'it_manager': [
      'Manage IT assets and helpdesk tickets',
      'Manage team members and assign roles',
      'Manage knowledge base and categories',
      'View and export IT reports',
      'Configure IT helpdesk settings',
    ],
    'helpdesk_manager': [
      'Manage and assign helpdesk tickets',
      'View all tickets across the team',
      'Close and reopen tickets',
      'View IT helpdesk settings',
      'Limited to ticket operations only',
    ],
    'helpdesk_agent': [
      'Create and edit tickets',
      'View own tickets only',
      'View asset information',
      'No management capabilities',
    ],
    'maintenance_manager': [
      'Manage work orders and PM schedules',
      'Manage maintenance assets',
      'Approve and assign work orders',
      'View and export maintenance reports',
      'Configure maintenance settings',
    ],
    'maintenance_support_manager': [
      'Manage and assign work orders',
      'Approve work orders',
      'View maintenance assets and PM schedules',
      'Create PM work orders',
      'Limited maintenance management',
    ],
    'maintenance_team': [
      'Create and edit work orders',
      'View own work orders only',
      'View maintenance assets',
      'View PM schedules',
      'No approval or management rights',
    ],
    'incident_manager': [
      'Manage incidents and investigations',
      'Create and verify corrective actions',
      'Triage and close incidents',
      'View and export incident reports',
      'Configure incident settings',
    ],
    'external_request_reviewer': [
      'Review and convert external requests',
      'Create tickets, incidents, or work orders',
      'Close external requests',
      'View own created items',
    ],
    'auditor': [
      'View-only access across all modules',
      'Export reports from all systems',
      'View all records (tickets, incidents, work orders)',
      'Access to knowledge base and user information',
      'No create, edit, or delete permissions',
    ],
    'general_user': [
      'Create tickets, incidents, and work orders',
      'View own submissions only',
      'Cannot view others\' items',
      'Basic end-user access',
    ],
  };
  return capabilities[slug] || ['Standard user permissions'];
};

</script>
